import React, { memo, useCallback } from 'react';
import { Dropdown, Icon } from 'semantic-ui-react';
import {
  HeaderSection,
  ButtonContainer,
  FilterGrid,
  FilterColumn,
  Label,
  DateFilterContainer,
  DateInputGroup,
  DateLabel,
  DateInput,
  ButtonGroup,
  PrimaryButton,
  SecondaryButton,
  ActiveButton,
  DownloadButton
} from '../styles';

interface FilterBarProps {
  franchise: string;
  setFranchise: (value: string) => void;
  estate: string;
  setEstate: (value: string) => void;
  manager: string;
  setManager: (value: string) => void;
  startDateInput: string;
  setStartDateInput: (value: string) => void;
  endDateInput: string;
  setEndDateInput: (value: string) => void;
  franchiseOptions: any[];
  estateOptions: any[];
  managerOptions: any[];
  handleSearch: () => void;
  handleClearFilters: () => void;
  handleDownload: () => void;
  isLoading: boolean;
  isDownloading?: boolean;
  franchisesLoading: boolean;
  managersLoading: boolean;
  activeMenu: string;
  setActiveMenu: (menu: string) => void;
  userRole: string;
}

const FilterBar: React.FC<FilterBarProps> = ({
  franchise,
  setFranchise,
  estate,
  setEstate,
  manager,
  setManager,
  startDateInput,
  setStartDateInput,
  endDateInput,
  setEndDateInput,
  franchiseOptions,
  estateOptions,
  managerOptions,
  handleSearch,
  handleClearFilters,
  handleDownload,
  isLoading,
  isDownloading = false,
  franchisesLoading,
  managersLoading,
  activeMenu,
  setActiveMenu,
  userRole
}) => {
  // Função para lidar com a alteração de menu com useCallback
  const handleMenuChange = useCallback(
    (menu: string) => {
      if (menu !== activeMenu) {
        setActiveMenu(menu);
      }
    },
    [setActiveMenu, activeMenu]
  );

  const handleFranchiseChange = useCallback(
    (value: string) => {
      setFranchise(value);
      setEstate('Todos');
      setManager('Todos');
    },
    [setFranchise]
  );

  const handleEstateChange = useCallback(
    (value: any) => {
      setEstate(value);
    },
    [setEstate]
  );

  const handleManagerChange = useCallback(
    (value: string) => {
      setManager(value);
    },
    [setManager]
  );

  const handleStartDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setStartDateInput(e.target.value);
    },
    [setStartDateInput]
  );

  const handleEndDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEndDateInput(e.target.value);
    },
    [setEndDateInput]
  );

  return (
    <>
      <HeaderSection>
        <ButtonContainer>
          <ActiveButton
            type="button"
            onClick={() => handleMenuChange('dashboard')}
            style={{
              backgroundColor: activeMenu === 'dashboard' ? '#59249B' : 'white',
              color: activeMenu === 'dashboard' ? 'white' : '#4b5563'
            }}
          >
            <svg
              width="26"
              height="20"
              viewBox="0 0 26 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M25.6407 15.8572V7.91797C25.6407 6.23634 24.0739 5.492 23.7672 5.36416L14.6681 0.430991C13.6238 -0.137119 12.436 -0.14369 11.2738 0.411875C9.35013 1.32826 2.80604 4.92808 2.03428 5.35341C1.09634 5.70348 0.0412273 6.57804 0.144049 7.88392C0.141657 8.58644 0.144049 9.58466 0.148233 10.6432V10.7059C0.154211 12.5698 0.16258 14.6809 0.144049 16.0185C0.131495 16.9307 0.397515 17.6457 0.934935 18.1404C1.5411 18.6983 2.28177 18.7819 2.66974 18.7819C2.72653 18.7819 2.77256 18.7801 2.80006 18.7778H4.86426C7.27279 18.7778 8.48572 17.3351 8.70152 17.0483C8.70989 17.0376 9.55757 15.985 9.92462 15.5638C10.2163 15.2281 10.5732 15.1278 10.9253 15.3058C11.5548 15.5555 12.2202 15.6809 12.8999 15.6809C15.8619 15.6809 18.2729 13.2723 18.2729 10.3117C18.2729 7.35165 15.8619 4.94301 12.8999 4.94301C9.93717 4.94301 7.52685 7.35165 7.52685 10.3117C7.52685 11.5387 7.95248 12.74 8.72902 13.6917C8.83603 13.8213 8.93227 14.2406 8.68299 14.614L7.48261 16.0734L7.45571 16.1087C7.42164 16.1553 6.61401 17.218 4.86426 17.218H2.73909L2.7098 17.2198C2.7098 17.2198 2.69485 17.2198 2.67213 17.2198C2.56931 17.2198 2.21661 17.2031 1.98825 16.9874C1.79277 16.8028 1.69832 16.4844 1.7049 16.04C1.72343 14.6809 1.71506 12.5572 1.70908 10.685V10.639C1.70669 9.56793 1.70251 8.55717 1.7049 7.85883L1.70071 7.78117C1.64213 7.16527 2.60279 6.80684 2.61116 6.80445L2.66556 6.78533L2.73311 6.75009C2.80186 6.71245 9.97483 2.76018 11.9428 1.8211C12.6709 1.47282 13.3004 1.46685 13.9191 1.80198L23.06 6.75606L23.1503 6.79848C23.1611 6.80027 24.0781 7.16945 24.0781 7.91558V15.9748L24.0924 16.0543C24.0924 16.0585 24.1552 16.5029 23.9077 16.8052C23.6267 17.1445 23.0289 17.2156 22.5776 17.2156H13.8755C12.4174 17.2156 10.8584 17.9892 9.96646 18.6499C9.79669 18.7754 9.69387 18.9414 9.66696 19.1278C9.63708 19.3291 9.70044 19.5496 9.8469 19.7419C9.97304 19.908 10.1745 20 10.4154 20C10.6252 20 10.8542 19.9307 11.0096 19.8196C11.7288 19.3017 12.8957 18.7778 13.8755 18.7778H22.58C23.7254 18.7778 24.5772 18.4486 25.1141 17.7987C25.7477 17.0316 25.6766 16.1087 25.6407 15.8572ZM9.0877 10.3099C9.0877 8.21128 10.7974 6.50277 12.8975 6.50277C14.9975 6.50277 16.7078 8.21128 16.7078 10.3099C16.7078 12.4085 14.9975 14.117 12.8975 14.117C10.7974 14.117 9.0877 12.4085 9.0877 10.3099Z"
                fill={activeMenu === 'dashboard' ? 'white' : '#AAADB1'}
              />
            </svg>
            Dashboard
          </ActiveButton>
          <ActiveButton
            type="button"
            onClick={() => handleMenuChange('seguroFianca')}
            style={{
              backgroundColor: activeMenu === 'seguroFianca' ? '#59249B' : 'white',
              color: activeMenu === 'seguroFianca' ? 'white' : '#4b5563'
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.9229 3.26953L10.4229 0.144534C10.1944 0.0497599 9.94948 0.000976563 9.70215 0.000976562C9.45481 0.000976563 9.2099 0.0497599 8.98145 0.144534L1.48145 3.26953C0.782227 3.5586 0.325195 4.24219 0.325195 5C0.325195 12.7539 4.79785 18.1133 8.97754 19.8555C9.43848 20.0469 9.95801 20.0469 10.4189 19.8555C13.7666 18.4609 19.0752 13.6445 19.0752 5C19.0752 4.24219 18.6182 3.5586 17.9229 3.26953ZM9.7041 17.4336L9.7002 2.55078L16.5713 5.41406C16.4424 11.3281 13.3643 15.6133 9.7041 17.4336Z"
                fill={activeMenu === 'seguroFianca' ? 'white' : '#AAADB1'}
              />
            </svg>
            Seguro Fiança
          </ActiveButton>
          <ActiveButton
            type="button"
            onClick={() => handleMenuChange('capitalizacao')}
            style={{
              backgroundColor: activeMenu === 'capitalizacao' ? '#59249B' : 'white',
              color: activeMenu === 'capitalizacao' ? 'white' : '#4b5563'
            }}
          >
            <svg
              width="18"
              height="20"
              viewBox="0 0 18 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.875 8.75H14.9375V5.9375C14.9375 2.66406 12.2734 0 9 0C5.72656 0 3.0625 2.66406 3.0625 5.9375V8.75H2.125C1.08984 8.75 0.25 9.58984 0.25 10.625V18.125C0.25 19.1602 1.08984 20 2.125 20H15.875C16.9102 20 17.75 19.1602 17.75 18.125V10.625C17.75 9.58984 16.9102 8.75 15.875 8.75ZM11.8125 8.75H6.1875V5.9375C6.1875 4.38672 7.44922 3.125 9 3.125C10.5508 3.125 11.8125 4.38672 11.8125 5.9375V8.75Z"
                fill={activeMenu === 'capitalizacao' ? 'white' : '#AAADB1'}
              />
            </svg>
            Capitalização
          </ActiveButton>
          <ActiveButton
            type="button"
            onClick={() => handleMenuChange('seguroIncendio')}
            style={{
              backgroundColor: activeMenu === 'seguroIncendio' ? '#59249B' : 'white',
              color: activeMenu === 'seguroIncendio' ? 'white' : '#4b5563'
            }}
          >
            <svg
              width="17"
              height="18"
              viewBox="0 0 17 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.6845 0.925629L9.7783 1.91C9.37984 1.97641 9.42578 2.38359 9.42578 2.53125H7.37505C7.75069 1.26942 6.80475 0 5.48828 0C4.1019 0 3.15503 1.39486 3.65214 2.67901C1.82265 3.13784 1.00203 4.58494 0.486359 5.87415C0.313285 6.30682 0.52373 6.79785 0.956398 6.97092C1.38942 7.1441 1.88027 6.93313 2.05313 6.50088C3.06454 3.9723 4.18624 4.21875 6.33203 4.21875V5.17064C4.87154 5.55251 3.80078 6.90156 3.80078 8.47986V17.1562C3.80078 17.6222 4.17854 18 4.64453 18H9.70703C10.173 18 10.5508 17.6222 10.5508 17.1562V8.4375C10.5508 6.86493 9.47514 5.54372 8.01953 5.16902V4.21875H9.42578C9.42578 4.3233 9.36591 4.77127 9.7783 4.84L15.6845 5.82437C15.9417 5.86723 16.1758 5.66895 16.1758 5.40823V1.34177C16.1758 1.08105 15.9417 0.882773 15.6845 0.925629ZM5.48828 2.53125C5.17814 2.53125 4.92578 2.2789 4.92578 1.96875C4.92578 1.6586 5.17814 1.40625 5.48828 1.40625C5.79843 1.40625 6.05078 1.6586 6.05078 1.96875C6.05078 2.2789 5.79843 2.53125 5.48828 2.53125Z"
                fill={activeMenu === 'seguroIncendio' ? 'white' : '#AAADB1'}
              />
            </svg>
            Seguro Incêndio
          </ActiveButton>
          <ActiveButton
            type="button"
            onClick={() => handleMenuChange('sinistros')}
            style={{
              backgroundColor: activeMenu === 'sinistros' ? '#59249B' : 'white',
              color: activeMenu === 'sinistros' ? 'white' : '#4b5563'
            }}
          >
            <svg
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.0381 0.932056C11.0381 0.00236861 9.84082 -0.348022 9.31348 0.422681C4.47559 7.49417 11.3506 7.81252 11.3506 11.25C11.3506 12.6418 10.2135 13.768 8.81738 13.7496C7.44355 13.7321 6.35059 12.5867 6.35059 11.2129V7.87268C6.35059 7.02502 5.3166 6.6137 4.73223 7.22815C3.68652 8.32659 2.60059 10.2082 2.60059 12.5C2.60059 16.6356 5.96504 20 10.1006 20C14.2361 20 17.6006 16.6356 17.6006 12.5C17.6006 5.84807 11.0381 4.96096 11.0381 0.932056Z"
                fill={activeMenu === 'sinistros' ? 'white' : '#AAADB1'}
              />
            </svg>
            Sinistros
          </ActiveButton>
        </ButtonContainer>
      </HeaderSection>

      <FilterGrid>
        <FilterColumn>
          <Label>Franquia</Label>
          <Dropdown
            placeholder="Selecione a franquia"
            fluid
            selection
            options={franchiseOptions}
            value={franchise}
            onChange={(_, { value }) => handleFranchiseChange(value as string)}
            loading={franchisesLoading}
            disabled={franchisesLoading}
          />
        </FilterColumn>
        <FilterColumn>
          <Label>Imobiliária</Label>
          <Dropdown
            placeholder="Selecione a imobiliária"
            fluid
            selection
            options={estateOptions.map(option => ({
              key: option.key,
              text: option.text,
              value: option.value.id || option.value
            }))}
            value={estate}
            onChange={(_, { value }) => handleEstateChange(value as string)}
            disabled={franchisesLoading}
          />
        </FilterColumn>
        {userRole.includes('BROKER') && (
          <FilterColumn>
            <Label>Gestor de conta</Label>
            <Dropdown
              placeholder="Selecione o gestor de conta"
              fluid
              selection
              options={managerOptions}
              value={manager}
              onChange={(_, { value }) => handleManagerChange(value as string)}
              loading={managersLoading}
              disabled={managersLoading}
            />
          </FilterColumn>
        )}
      </FilterGrid>

      <DateFilterContainer>
        <DateInputGroup>
          <DateLabel>Data inicial:</DateLabel>
          <DateInput
            type="text"
            value={startDateInput}
            onChange={handleStartDateChange}
            placeholder="DD/MM/AAAA"
          />
        </DateInputGroup>
        <DateInputGroup>
          <DateLabel>Data final:</DateLabel>
          <DateInput
            type="text"
            value={endDateInput}
            onChange={handleEndDateChange}
            placeholder="DD/MM/AAAA"
          />
        </DateInputGroup>
        <ButtonGroup>
          <PrimaryButton onClick={handleSearch} disabled={isLoading}>
            {isLoading ? 'Carregando...' : 'Buscar'}
          </PrimaryButton>
          <SecondaryButton onClick={handleClearFilters} disabled={isLoading}>
            Limpar filtros
          </SecondaryButton>
          <DownloadButton onClick={handleDownload} disabled={isLoading || isDownloading}>
            {isDownloading ? <Icon name="spinner" loading /> : <Icon name="download" />}
          </DownloadButton>
        </ButtonGroup>
      </DateFilterContainer>
    </>
  );
};

export default memo(FilterBar);
